import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";

@Injectable()
export class StandardDesignParameters {
	// Width Parameters
	static MIN_MD_WIDTH = 560;
	static EXT_MOBILE = 660;
	static MAX_MD_WIDTH = 1120;
	static FOOTER_EXTENDED_MIN_WIDTH = 1200;
	static MIN_WIDTH_COL6 = 1310;
	static MIN_WD_COL4_MTMA = 1367;
	static LG_WIDTH = 1500;
	static MIN_WIDTH_COL4 = 1530;
	static MIN_WIDTH_MX7 = 1700;
	static MIN_WIDTH_MX7_LATER = 1800;

	// Height Parameters
	static LG_HEIGHT = 680;
	static LARGE_DESKTOP = 905;
	static MIN_FIXED_FOOTER_HEIGHT = 1024;
}

@Injectable()
export class StandardFooterLayout {
	static getCopyrightInfo(translate: TranslateService): string {
		return translate.instant('COPYRIGHT') + ' ' + new Date().getFullYear().toString() + ' CAME S.p.A - ' + translate.instant('ALL_RIGHT_RESERVED') + ' - ' + translate.instant('VAT_CODE') + ' 03481280265';
	}
}

@Injectable()
export class StandardMtmaLayout {
	static actionLayout(screenWidth: number, position: string): string {
		switch (position) {
			case 'left':
				return (screenWidth <= StandardDesignParameters.MIN_WD_COL4_MTMA) ? (StandardPageLayout.onExtendedMobile(screenWidth) ? 'col-12 flex justify-content-center' : 'col-12 flex justify-content-center px-8') : 'col-4 flex align-items-end justify-content-start pr-8';
			case 'center': // optimized for memory component
				return (screenWidth <= StandardDesignParameters.MIN_WD_COL4_MTMA) ? (screenWidth <= StandardDesignParameters.MAX_MD_WIDTH ? (StandardPageLayout.onExtendedMobile(screenWidth) ? 'col-12' : 'col-12 px-8') : 'col-6') : 'col-4';
			case 'right':
				return (screenWidth <= StandardDesignParameters.MIN_WD_COL4_MTMA) ? (screenWidth <= StandardDesignParameters.MAX_MD_WIDTH ? (StandardPageLayout.onExtendedMobile(screenWidth) ? 'col-12 flex justify-content-center' : 'col-12 flex justify-content-center px-8') : 'col-6 flex align-items-end justify-content-end') : 'col-4 flex align-items-end justify-content-end';
			default:
				return "flex justify-content-center";
		}
	}

	static underMinWidthCol4Mtma(screenWidth: number): boolean {
		return screenWidth <= StandardDesignParameters.MIN_WD_COL4_MTMA;
	}

	static dynamicInputSearchWidth(screenWidth: number, widthRemSizeDesktop: string): string {
		return (screenWidth <= StandardDesignParameters.MIN_WD_COL4_MTMA) ? "w-full h-2_5rem" : widthRemSizeDesktop + " h-2rem";
	}
}

@Injectable()
export class StandardTruncateString {
	/* aux func of getSplitString(), don't call this from components */
	static splitString(originalStr: string, num: number): string {
		return (originalStr.length > num) ? originalStr.slice(0, num) + "- \n" + originalStr.slice(num, originalStr.length): originalStr;
	}

	/* split here and truncate in html (for example: getSplitString(site.Name) | truncate:[37])
	*	splitting and go to newline could consent us to show at least two rows, also for strings which doesn't have spaces inside
	*/
	static getSplitString(screenWidth: number, originalStr: string, mobileTruncate: number, tabletTruncate: number, desktopTruncate: number): string {
		if (originalStr === undefined || originalStr === '') {
			return '';
		}
		if (StandardPageLayout.onExtendedMobile(screenWidth)) {
			return StandardTruncateString.splitString(originalStr, mobileTruncate);
		}
		if (StandardPageLayout.onTablet(screenWidth)) {
			return StandardTruncateString.splitString(originalStr, tabletTruncate);
		}
		if (StandardPageLayout.overTablet(screenWidth)) {
			return StandardTruncateString.splitString(originalStr, desktopTruncate);
		}
		return originalStr;
	}
}

@Injectable()
export class StandardPageLayout {
	static onExtendedMobile(screenWidth: number): boolean {
		return screenWidth <= StandardDesignParameters.EXT_MOBILE;
	}

	static underTablet(screenWidth: number): boolean {
		return screenWidth <= StandardDesignParameters.MAX_MD_WIDTH;
	}

	static overTablet(screenWidth: number): boolean {
		return screenWidth > StandardDesignParameters.MAX_MD_WIDTH;
	}

	static onTablet(screenWidth: number): boolean {
		return screenWidth > StandardDesignParameters.EXT_MOBILE && screenWidth <= StandardDesignParameters.MAX_MD_WIDTH;
	}

	static tabletUnderLgWidth(screenWidth: number): boolean {
		return screenWidth > StandardDesignParameters.EXT_MOBILE && screenWidth <= StandardDesignParameters.LG_WIDTH;
	}

	static tabletUnderMinWdCol6(screenWidth: number): boolean {
		return screenWidth > StandardDesignParameters.EXT_MOBILE && screenWidth <= StandardDesignParameters.MIN_WIDTH_COL6;
	}

	static underMinWidthCol4(screenWidth: number): boolean {
		return screenWidth <= StandardDesignParameters.MIN_WIDTH_COL4;
	}

	static colFilterResponsive(screenWidth: number, elemType: string): string {
		switch (elemType) {
			case 'button':
				return (screenWidth >= StandardDesignParameters.LG_WIDTH) ? 'col-3' : 'col-12 flex justify-content-center';
			case 'empty':
				return (screenWidth >= StandardDesignParameters.LG_WIDTH) ? 'col-1' : '';
			case 'filter':
				return (screenWidth >= StandardDesignParameters.LG_WIDTH) ? 'col-4' : (StandardPageLayout.onExtendedMobile(screenWidth) ? 'col-12 pt-0' : 'col-12 px-6');
			default:
				return '';
		}
	}

	static col2ResponsiveStd(screenWidth: number): string {
		return (screenWidth >= StandardDesignParameters.MAX_MD_WIDTH) ? "col-2" : (!StandardPageLayout.onExtendedMobile(screenWidth)) ? 'col-6' : 'col-12';
	}

	static col3ResponsiveStd(screenWidth: number): string {
		return (screenWidth >= StandardDesignParameters.MIN_WIDTH_COL4) ? "col-3" : (!StandardPageLayout.onExtendedMobile(screenWidth)) ? 'col-6' : 'col-12';
	}

	/* Registration col4 */
	static col4ResponsiveReg(fieldPosition: string, screenWidth: number): string {
		if (screenWidth >= StandardDesignParameters.MIN_WIDTH_COL4) { // >= 1530
			switch (fieldPosition) {
				case 'left':
					return "col-4 pl-6 pr-3";
				case 'center':
					return "col-4 px-5";
				case 'right':
					return "col-4 pl-3 pr-6";
				default:
					return "col-4";
			}
		} else { // < 1530
			return (screenWidth >= StandardDesignParameters.MAX_MD_WIDTH) ? "col-6 px-6" : "col-12";
		}
	}

	static col4ResponsiveStd(screenWidth: number): string {
		return (screenWidth >= StandardDesignParameters.MIN_WIDTH_MX7_LATER) ? "col-4 px-4" : (screenWidth >= StandardDesignParameters.MIN_WIDTH_COL6) ? 'col-6' : 'col-12';
	}

	static col6Responsive(screenWidth: number): string {
		return (screenWidth >= StandardDesignParameters.MAX_MD_WIDTH) ? "col-6 px-6" : "col-12";
	}

	/* used on main filter as supportReq (TECH) or supportDevice, col12 starts before due to long strings */
	static col6ResponsiveMainFilter(screenWidth: number, alignment: string): string {
		if (alignment === 'right') {
			alignment = ' flex justify-content-end';
		} else {
			alignment = '';
		}
		return (screenWidth >= StandardDesignParameters.MIN_WIDTH_COL6) ? "col-6 px-5" + alignment : "col-12";
	}

	static col6ResponsiveButtonLayout(screenWidth: number, position: string): string {
		switch (position) {
			case "left":
				return (screenWidth >= StandardDesignParameters.MAX_MD_WIDTH) ? "col-6 flex justify-content-start" : "col-12 flex justify-content-center";
			case "right":
				return (screenWidth >= StandardDesignParameters.MAX_MD_WIDTH) ? "col-6 flex justify-content-end" : "col-12 flex justify-content-center pt-0";
			default:
				return "col-12";
		}
	}

	static col6ResponsiveDesktopTablet(screenWidth: number): string {
		return (screenWidth >= StandardDesignParameters.EXT_MOBILE) ? "col-6" : "col-12";
	}

	static col6ResponsiveNoPadding(screenWidth: number): string {
		return (screenWidth >= StandardDesignParameters.MAX_MD_WIDTH) ? "col-6" : "col-12";
	}

	/* use in header page with a button on left and search on the right */
	static col6HeaderPage(screenWidth: number, position: string): string {
		let retStr = "flex align-items-center";
		if (screenWidth >= StandardDesignParameters.MIN_WIDTH_COL6) {
			switch (position) {
				case "left":
					retStr += " justify-content-start";
					break;
				case "right":
					retStr += " justify-content-end";
					break;
				default:
					break;
			}
			retStr += " col-6";
		} else {
			retStr += " justify-content-center col-12";
		}

		return retStr;
	}

	static customPaddingX(screenWidth: number): string {
		return (screenWidth >= StandardDesignParameters.MAX_MD_WIDTH) ? "px-6" : "";
	}

	static layoutMarginResponsive(screenWidth: number): string {
		return (screenWidth >= StandardDesignParameters.MIN_WIDTH_MX7_LATER) ? "mx-4" : "mx-2";
	}

	static paddingTopResponsive(screenWidth: number): string {
		return StandardPageLayout.onExtendedMobile(screenWidth) ? 'pt-3' : 'pt-0';
	}

	// classe simile a newShowErrors che gestisce gli inputText tradizionali senza validatori
	// in ogni borderRow in pagina va aggiunto 'border-bottom-1 border-100'
	static simpleInputTextLayout(classType: string, screenWidth: number) {
		switch (classType) {
			case 'borderRow':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					return "row";
				}
				return "justify-content-between";
			case 'borderInput':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					return "col-12 w-full p-0";
				}
				return "pl-2";
			case 'label':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					return "col-12 p-1 justify-content-center";
				}
				return "pr-2";
			default:
				return "";
		}
	}

	// gestisce componenti complessi come i colori delle schedulazioni, radiobutton, checkbox ecc.
	static manageRespStyle(classType: string, screenWidth: number): string {
		switch (classType) {
			case 'borderRow':
				if (screenWidth <= StandardDesignParameters.MAX_MD_WIDTH) {
					return 'row border-100 border-bottom-1';
				}
				return "border-100 border-bottom-1";
			case 'borderInput':
				if (screenWidth <= StandardDesignParameters.MAX_MD_WIDTH) {
					return 'grid col-12 w-full p-0';
				}
				return "";
			case 'label':
				if (screenWidth <= StandardDesignParameters.MAX_MD_WIDTH) {
					return 'col-12 p-1 mb-2';
				}
				return "";
			default:
				return "";
		}
	}

	static dynamicInputTextWidth(screenWidth: number, widthRemSizeDesktop: string) {
		return StandardPageLayout.onExtendedMobile(screenWidth) ? 'w-full h-2_5rem' : widthRemSizeDesktop + ' h-2rem';
	}

	static dynamicInputTextDesktopTabletWidth(screenWidth: number, widthRemSizeDesktop: string, widthRemSizeTablet: string) {
		return StandardPageLayout.onExtendedMobile(screenWidth) ? 'w-full h-2_5rem' : screenWidth <= StandardDesignParameters.MAX_MD_WIDTH ? widthRemSizeTablet + ' h-2rem' : widthRemSizeDesktop + ' h-2rem';
	}

	static dynamicButtonWidth(screenWidth: number): string {
		return StandardPageLayout.onExtendedMobile(screenWidth) ? "w-full" : "w-16rem";
	}
}

@Injectable()
export class StandardNewValidator {
	static firstCompile: boolean;

	static setFirstCompile(frstComp: boolean) {
		StandardNewValidator.firstCompile = frstComp;
	}

	static getFirstCompile(): boolean {
		return StandardNewValidator.firstCompile;
	}

	// use only for 'phone' field on registration & profile pages -> not required and could be === ""
	static needsToBeDirty(type: any, classType: string, screenWidth: number): string {
		let inputTextStyle = "";

		switch (classType) {
			case 'borderRow':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					return "row";
				}
				return "border-bottom-1 border-100";
			case 'borderInput':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					inputTextStyle += "col-12 w-full p-0";
				}
				if (type.value !== "" && type.errors !== null && !StandardNewValidator.getFirstCompile() && type.dirty) {
					inputTextStyle += " border-bottom-2 border-50";
				} else {
					inputTextStyle += " border-bottom-1 border-100";
				}
				return inputTextStyle;
			case 'label':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					inputTextStyle += "col-12 p-1";
				}
				if (type.value !== "" && type.errors !== null && !StandardNewValidator.getFirstCompile() && type.dirty) {
					inputTextStyle += " text-50";
				}
				return inputTextStyle;
			default:
				return inputTextStyle;
		}
	}

	static newShowErrors(type: any, classType: string, screenWidth: number): string {
		let inputTextStyle = "";

		switch (classType) {
			case 'borderRow':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					return "row";
				}
				return "border-bottom-1 border-100";
			case 'borderInput':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					inputTextStyle += "col-12 w-full p-0";
				}
				if (type.errors !== null && !StandardNewValidator.getFirstCompile()) {
					inputTextStyle += " border-bottom-2 border-50";
				} else {
					inputTextStyle += " border-bottom-1 border-100";
				}
				return inputTextStyle;
			case 'label':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					inputTextStyle += "col-12 p-1";
				}
				if (type.errors !== null && !StandardNewValidator.getFirstCompile()) {
					inputTextStyle += " text-50";
				}
				return inputTextStyle;
			default:
				return inputTextStyle;
		}
	}

	static newShowErrorsCustomColor(type: any, classType: string, screenWidth: number, borderColors: string): string {
		let inputTextStyle = "";

		switch (classType) {
			case 'borderRow':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					return "row";
				}
				return "border-bottom-1 " + borderColors;
			case 'borderInput':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					inputTextStyle += "col-12 w-full p-0";
				}
				if (type.errors !== null && !StandardNewValidator.getFirstCompile()) {
					inputTextStyle += " border-bottom-2 border-50";
				}
				return inputTextStyle;
			case 'label':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					inputTextStyle += "col-12 p-1";
				}
				if (type.errors !== null && !StandardNewValidator.getFirstCompile()) {
					inputTextStyle += " text-50";
				}
				return inputTextStyle;
			default:
				return inputTextStyle;
		}
	}

	static manageRespStylewithError(type: any, classType: string, screenWidth: number): string {
		let inputTextStyle = "";

		switch (classType) {
			case 'borderRow':
				if (screenWidth <= StandardDesignParameters.MAX_MD_WIDTH) {
					inputTextStyle += "row";
				}
				inputTextStyle += " border-100 border-bottom-1";
				return inputTextStyle;
			case 'borderInput':
				if (screenWidth <= StandardDesignParameters.MAX_MD_WIDTH) {
					inputTextStyle += "grid col-12 w-full p-0";
				}
				return inputTextStyle;
			case 'label':
				if (screenWidth <= StandardDesignParameters.MAX_MD_WIDTH) {
					inputTextStyle += "col-12 p-1 mb-2";
				}
				if (type.errors !== null && !StandardNewValidator.getFirstCompile()) {
					inputTextStyle += " text-50";
				}
				return inputTextStyle;
			default:
				return inputTextStyle;
		}
	}

	static showErrorsConfirmFieldsCustomColor(type: any, classType: string, notEquivalentType: string, screenWidth: number, form: any, borderColors: string): string {
		let inputTextStyle = "";

		switch (classType) {
			case 'borderRow':
				return StandardNewValidator.newShowErrorsCustomColor(type, 'borderRow', screenWidth, borderColors);
			case 'borderInput':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					inputTextStyle += "col-12 w-full p-0";
				}
				if (StandardNewValidator.showErrorConfirmInvalidMessage(type, notEquivalentType, form)) {
					inputTextStyle += " border-bottom-2 border-50";
				}
				return inputTextStyle;
			case 'label':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					inputTextStyle += "col-12 p-1";
				}
				if (StandardNewValidator.showErrorConfirmInvalidMessage(type, notEquivalentType, form)) {
					inputTextStyle += " text-50";
				}
				return inputTextStyle;
			default:
				return inputTextStyle;
		}
	}

	static showErrorsConfirmFields(type: any, classType: string, notEquivalentType: string, screenWidth: number, form: any): string {
		let inputTextStyle = "";

		switch (classType) {
			case 'borderRow':
				return StandardNewValidator.newShowErrors(type, 'borderRow', screenWidth);
			case 'borderInput':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					inputTextStyle += "col-12 w-full p-0";
				}
				if (StandardNewValidator.showErrorConfirmInvalidMessage(type, notEquivalentType, form)) {
					inputTextStyle += " border-bottom-2 border-50";
				} else {
					inputTextStyle += " border-bottom-1 border-100";
				}
				return inputTextStyle;
			case 'label':
				if (StandardPageLayout.onExtendedMobile(screenWidth)) {
					inputTextStyle += "col-12 p-1";
				}
				if (StandardNewValidator.showErrorConfirmInvalidMessage(type, notEquivalentType, form)) {
					inputTextStyle += " text-50";
				}
				return inputTextStyle;
			default:
				return inputTextStyle;
		}
	}

	static showErrorRequiredMessage(type: any): boolean {
		return (type.errors !== null && !StandardNewValidator.getFirstCompile());
	}

	static showErrorInvalidMessage(type: any): boolean {
		return (type.errors !== null && type.dirty && !StandardNewValidator.getFirstCompile());
	}

	static showErrorConfirmInvalidMessage(type: any, notEquivalentType: string, form: any): boolean {
		return (type.errors !== null && type.errors !== undefined && !StandardNewValidator.getFirstCompile()) || (!StandardNewValidator.getFirstCompile() && form.errors !== null && form.errors[notEquivalentType] !== null && form.errors[notEquivalentType]);
	}

	static showErrorRequiredCheckBox(type: any): string {
		return (type.errors !== null && !StandardNewValidator.getFirstCompile()) ? 'ng-invalid ng-dirty' : '';
	}

	static justifyMessagesNewValidator(screenWidth: number): string {
		return StandardPageLayout.onExtendedMobile(screenWidth) ? "justify-content-center text-center" : "justify-content-end text-right";
	}

	static showDoubleErrorRequiredMessageAndCheck(typeWs: any, typeSp: any): boolean {
		if (typeSp === null && typeWs === null) {
			return true;
		}

		let spErr, wsErr = false;
		if (typeSp === null) {
			spErr = true;
		} else {
			spErr = typeSp.length === 0;
		}
		if (typeWs === null) {
			wsErr = true;
		} else {
			wsErr = typeWs.length === 0;
		}
		return spErr && wsErr && !StandardNewValidator.getFirstCompile();
	}
}

@Injectable()
export class StandardNewValidatorStepTabs {
	static firstTabfields: any[] = [];
	static secondTabfields: any[] = [];
	static thirdTabfields: any[] = [];

	static pushFieldOnTab(field: any, tab: number) {
		switch (tab) {
			case 0:
				StandardNewValidatorStepTabs.firstTabfields.push(field);
				break;
			case 1:
				StandardNewValidatorStepTabs.secondTabfields.push(field);
				break;
			case 2:
				StandardNewValidatorStepTabs.thirdTabfields.push(field);
				break;
			default:
				break;
		}
	}

	static getFirstTabErrors(): boolean {
		let tabError = false;

		StandardNewValidatorStepTabs.firstTabfields.forEach(element => {
			if (element.errors && !tabError) {
				tabError = true;
			}
		});
		return tabError;
	}

	static getSecondTabErrors(): boolean {
		let tabError = false;

		StandardNewValidatorStepTabs.secondTabfields.forEach(element => {
			if (element.errors && !tabError) {
				tabError = true;
			}
		});
		return tabError;
	}

	static getThirdTabErrors(): boolean {
		let tabError = false;

		StandardNewValidatorStepTabs.thirdTabfields.forEach(element => {
			if (element.errors && !tabError) {
				tabError = true;
			}
		});
		return tabError;
	}

	static canSwitchTab(activeIndex: number): boolean {
		if (!StandardNewValidator.getFirstCompile()) {
			switch (activeIndex) {
				case 0:
					return !StandardNewValidatorStepTabs.getFirstTabErrors();
				case 1:
					return !StandardNewValidatorStepTabs.getSecondTabErrors();
				case 2:
					return !StandardNewValidatorStepTabs.getThirdTabErrors();
				default:
					return true;
			}
		}
		return true;
	}

	static getColorFirstTab(): string {
		let colorTab = "border-left-none border-right-none border-top-none border-bottom-2";

		if (StandardNewValidatorStepTabs.getFirstTabErrors() && !StandardNewValidator.getFirstCompile()) {
			colorTab = colorTab + " text-50 border-50";
		} else {
			colorTab = colorTab + " text-primary border-primary";
		}
		return colorTab;
	}

	static getColorSecondTab(activeIndex: number): string {
		let colorTab = "border-left-none border-right-none border-top-none border-bottom-2";

		if (StandardNewValidatorStepTabs.getSecondTabErrors() && !StandardNewValidator.getFirstCompile()) {
			colorTab = colorTab + " text-50 border-50";
		} else if (activeIndex >= 1) {
			colorTab = colorTab + " text-primary border-primary";
		} else {
			colorTab = colorTab + " text-color border-900";
		}
		return colorTab;
	}

	static getColorThirdTab(activeIndex: number): string {
		let colorTab = "border-left-none border-right-none border-top-none border-bottom-2";

		if (StandardNewValidatorStepTabs.getThirdTabErrors() && !StandardNewValidator.getFirstCompile()) {
			colorTab = colorTab + " text-50 border-50";
		} else if (activeIndex >= 2) {
			colorTab = colorTab + " text-primary border-primary";
		} else {
			colorTab = colorTab + " text-color border-900";
		}
		return colorTab;
	}

	static newShowErrorsWithTabValidation(type: any, classType: string, screenWidth: number): string {
		return StandardNewValidator.newShowErrors(type, classType, screenWidth);
	}
}

@Injectable()
export class StandardDynamicComponent {
	static dynamicDatepicker(screenWidth: number): string {
		return StandardPageLayout.onExtendedMobile(screenWidth) ? "p-datepicker" : "p-datepicker p-datepicker-right-desktop";
	}

	static dynamicAutocomplete(screenWidth: number): string {
		return StandardPageLayout.onExtendedMobile(screenWidth) ? "p-autocomplete-panel" : "p-autocomplete-panel-right";
	}
}